import { useState, useEffect, useMemo } from 'react';
import { BREAKPOINTS, type Breakpoint } from './breakpoints';

interface ResponsiveState {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  breakpoint: Breakpoint;
  /**
   * True when the primary input is touch/stylus (phones, tablets including iPad Pro
   * in landscape). False for mouse-based laptops/desktops. Use this for features
   * that depend on physical capabilities (e.g., camera upload) rather than screen size.
   */
  isTouchPrimary: boolean;
}

function getState(mobileMatch: boolean, tabletMatch: boolean, touchMatch: boolean): ResponsiveState {
  const base = mobileMatch
    ? { isMobile: true, isTablet: false, isDesktop: false, breakpoint: 'mobile' as const }
    : tabletMatch
      ? { isMobile: false, isTablet: true, isDesktop: false, breakpoint: 'tablet' as const }
      : { isMobile: false, isTablet: false, isDesktop: true, breakpoint: 'desktop' as const };
  return { ...base, isTouchPrimary: touchMatch };
}

const mobileMql = typeof window !== 'undefined'
  ? window.matchMedia(`(max-width: ${BREAKPOINTS.mobile}px)`)
  : null;

const tabletMql = typeof window !== 'undefined'
  ? window.matchMedia(`(max-width: ${BREAKPOINTS.tablet}px)`)
  : null;

const touchMql = typeof window !== 'undefined'
  ? window.matchMedia('(pointer: coarse)')
  : null;

export function useResponsive(): ResponsiveState {
  const [state, setState] = useState<ResponsiveState>(() =>
    getState(
      mobileMql?.matches ?? false,
      tabletMql?.matches ?? false,
      touchMql?.matches ?? false,
    )
  );

  useEffect(() => {
    if (!mobileMql || !tabletMql || !touchMql) return;

    const handler = () => {
      setState(getState(mobileMql.matches, tabletMql.matches, touchMql.matches));
    };

    mobileMql.addEventListener('change', handler);
    tabletMql.addEventListener('change', handler);
    touchMql.addEventListener('change', handler);

    return () => {
      mobileMql.removeEventListener('change', handler);
      tabletMql.removeEventListener('change', handler);
      touchMql.removeEventListener('change', handler);
    };
  }, []);

  return useMemo(() => state, [state.breakpoint, state.isTouchPrimary]);
}
